import React, { useMemo, useState } from 'react';

import { Alert, Box, CircularProgress, Link, Stack, Typography } from '@mui/material';

import { useGetFxRates } from '~/hooks/apis/fx/useGetFxRates';
import { RATE_PLAN_ACTIVE_STATUS, RatePlan } from '~/hooks/apis/reservation/usePropertyRatePlans';

import currencyFormatter from '~/utils/currencyFormatter';

import { CARD_SX, HIGHLIGHTED_CARD_SX } from './constants';

interface RatePlanInfoProps {
  ratePlans: Array<RatePlan>;
  isLoading: boolean;
  error?: unknown;
  currency: string;
}

const INITIAL_VISIBLE_COUNT = 2;

const cardStyles = {
  highlighted: HIGHLIGHTED_CARD_SX,
  default: { ...CARD_SX, p: 3 },
};

const labelStyles = {
  fontSize: 11,
  fontWeight: 600,
  color: 'text.secondary',
  letterSpacing: 0.5,
  mb: 1,
};

function convertCurrency(
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  ratesMap: Record<string, number>,
): number | null {
  const fromRate = ratesMap[fromCurrency];
  const toRate = ratesMap[toCurrency];
  if (fromRate == null || toRate == null) {
    return null;
  }
  return (amount * toRate) / fromRate;
}

export default function RatePlanInfo({ ratePlans, isLoading, error, currency }: RatePlanInfoProps) {
  const [showAllPlans, setShowAllPlans] = useState(false);

  const needsFxConversion = ratePlans.some((p) => p.costCurrency && p.costCurrency !== currency && p.price != null);
  const { data: fxRates = [] } = useGetFxRates(needsFxConversion);

  const fxRatesMap = useMemo(() => {
    const map: Record<string, number> = { AUD: 1 };
    for (const rate of fxRates) {
      map[rate.currency] = rate.rate;
    }
    return map;
  }, [fxRates]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Unable to load rate plans
      </Alert>
    );
  }

  if (ratePlans.length === 0) {
    return (
      <Box sx={cardStyles.default}>
        <Typography variant="body2" color="text.secondary">
          No rate plan data available
        </Typography>
      </Box>
    );
  }

  const activePlans = ratePlans.filter((plan) => plan.status?.toLowerCase() === RATE_PLAN_ACTIVE_STATUS);
  const plansWithPrice = activePlans.filter((p) => p.price != null).sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
  const cheapestPlan = plansWithPrice[0] ?? activePlans[0] ?? ratePlans[0];
  const priceCurrency = cheapestPlan?.costCurrency || currency;
  const showConversion = cheapestPlan?.price != null && priceCurrency !== currency;
  const convertedPrice = showConversion
    ? convertCurrency(cheapestPlan.price, priceCurrency, currency, fxRatesMap)
    : null;
  const visiblePlans = showAllPlans ? activePlans : activePlans.slice(0, INITIAL_VISIBLE_COUNT);

  return (
    <Stack spacing={2}>
      <Typography sx={{ fontSize: 22, fontWeight: 700 }}>Your Rate Strategy</Typography>
      <Box sx={cardStyles.highlighted}>
        <Typography sx={labelStyles}>CHEAPEST LPC RATE</Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{cheapestPlan.name ?? '—'}</Typography>
        {cheapestPlan.rate_plan_code && (
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>(Code: {cheapestPlan.rate_plan_code})</Typography>
        )}
        <Typography sx={{ fontSize: 32, fontWeight: 700, color: 'success.main', mt: 1 }}>
          {cheapestPlan.price != null ? currencyFormatter(priceCurrency, cheapestPlan.price, 0) : '—'}
        </Typography>
        {convertedPrice != null && (
          <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
            ≈ {currencyFormatter(currency, convertedPrice, 0)} {currency}
          </Typography>
        )}
      </Box>

      <Box sx={cardStyles.default}>
        <Typography sx={labelStyles}>ACTIVE RATE PLANS ({activePlans.length})</Typography>
        <Stack spacing={0.5}>
          {visiblePlans.map((plan) => (
            <Typography key={plan.id} variant="body2">
              {plan.name ?? plan.rate_plan_code ?? plan.id}
            </Typography>
          ))}
        </Stack>
        {activePlans.length > INITIAL_VISIBLE_COUNT && (
          <Link
            component="button"
            onClick={() => setShowAllPlans((prev) => !prev)}
            sx={{ mt: 1, fontSize: 13, color: 'primary.main', textDecoration: 'none', cursor: 'pointer' }}
          >
            {showAllPlans ? 'Show less' : 'Show all'}
          </Link>
        )}
      </Box>

      <Box sx={cardStyles.default}>
        <Typography sx={labelStyles}>CURRENT PROMOTIONS (0)</Typography>
        <Typography variant="body2" color="text.secondary">
          No promotions data
        </Typography>
      </Box>
    </Stack>
  );
}
